'use client';

import { useEffect, useSyncExternalStore } from 'react';
import type { Subscriptions } from '@/lib/types';
import { isUnauthorized, type ProfileApi } from '@/lib/api';

/**
 * One person's subscriptions, fetched once and shared by the card on the
 * page and the page in the sheet, so opening the sheet never refetches
 * and a toggle in the sheet shows on the card behind it at once.
 */
type Entry = Subscriptions | 'error';
const cache = new Map<number, Entry>();
const pending = new Set<number>();
const listeners = new Set<() => void>();

function emit() {
  for (const l of listeners) l();
}
function subscribe(l: () => void) {
  listeners.add(l);
  return () => listeners.delete(l);
}

export function setSubscriptions(contactId: number, value: Entry) {
  cache.set(contactId, value);
  emit();
}

/** The cached entry, fetching it the first time anyone asks. `null` while loading. */
export function useSubscriptions(api: ProfileApi, contactId: number): Entry | null {
  const entry = useSyncExternalStore(
    subscribe,
    () => cache.get(contactId) ?? null,
    () => null
  );
  useEffect(() => {
    if (cache.has(contactId) || pending.has(contactId)) return;
    pending.add(contactId);
    api
      .getSubscriptions(contactId)
      .then((s) => setSubscriptions(contactId, s))
      .catch((err) => {
        setSubscriptions(
          contactId,
          isUnauthorized(err)
            ? {
                publications: [],
                bulkEmailOptOut: false,
                texting: { optedIn: false, typeId: null },
              }
            : 'error'
        );
      })
      .finally(() => pending.delete(contactId));
  }, [api, contactId]);
  return entry;
}
